import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CLIENTS, getClientsByRep, getMissingCategories, getDocumentsByClient, getRepById, DOC_CATEGORIES } from '../../data/mockData';
import StatusBadge from '../../components/shared/StatusBadge';
import { Search, AlertCircle, FileText, ArrowRight, Plus, X, CheckCircle2, Send, Bell, Trash2, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { SkeletonClients } from '../../components/Skeleton';
import { useLanguage } from '../../context/LanguageContext';
import { INDUSTRIES } from '../../data/industries';
import { matchesClientSearch } from '../../utils/clientSearch';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

const inputCls = "w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all";

const EMPTY_FORM = { businessName: '', ownerName: '', email: '', phone: '', industry: '', state: '', requestedAmount: '', pullerId: '' };

// Matched on "zoho.com/.../tab/<Module>/<id>" rather than a fixed domain -
// crm.zoho.com/crm/org.../tab/Accounts/<id> was the only shape this accepted,
// which rejected a record opened from inside Zoho One instead of the CRM app
// directly (one.zoho.com/zohoone/<org>/home/cxapp/crm/org.../tab/Accounts/<id>)
// even though extractZohoId below already parses that URL's id correctly -
// this was the only thing standing in its way. The /tab/<Module>/<id> segment
// is what every genuine CRM record link actually has in common, on either host.
const ZOHO_URL_REGEX = /zoho\.com\/.*\/tab\/(Accounts|Deals|Leads)\/\d+/i;
function extractZohoId(url) {
  try { const m = url.match(/\/([0-9]{15,20})(?:[/?#]|$)/); return m ? m[1] : ''; } catch { return ''; }
}

// Every client value this page prints goes through here. One record holding
// something other than text - a Zoho lookup arriving as { name, id }, a list,
// a number - used to take the WHOLE Clients page down with "Something went
// wrong", because React cannot print an object and .split/.slice do not exist
// on one. A Zoho-style object shows its name; anything else becomes text.
function txt(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(txt).filter(Boolean).join(', ');
  if (typeof v === 'object') return txt(v.name ?? v.display_value ?? v.full_name ?? v.value ?? '');
  return String(v);
}

export default function ClientsPage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const repFilter = searchParams.get('rep');
  const [repSearch, setRepSearch] = useState('');
  const [showRepDropdown, setShowRepDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [addMode, setAddMode] = useState('manual');
  const [zohoUrlInput, setZohoUrlInput] = useState('');
  const [zohoImporting, setZohoImporting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [realClients, setRealClients] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [deletedClients, setDeletedClients] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [allDocs, setAllDocs] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  // Deleted Clients rows ticked for "Delete selected forever".
  const [selectedDeleted, setSelectedDeleted] = useState(() => new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [permDeleteConfirm, setPermDeleteConfirm] = useState(null);
  const [reminderModal, setReminderModal] = useState(null); // { client, category }
  const [reminderMsg, setReminderMsg] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [selectedClients, setSelectedClients] = useState(new Set());

  const toggleClientSelect = (id, e) => {
    e.stopPropagation();
    setSelectedClients(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { t } = useLanguage();
  const [sortBy, setSortBy] = useState('recent');
  const [visibleCount, setVisibleCount] = useState(50);

  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const [reps, setReps] = useState([]);
  // Staff who can be picked as puller in Add Client (ids + names, any staff role).
  const [pullers, setPullers] = useState([]);
  useEffect(() => {
    if (!token || user.role === 'client') return;
    fetch(`${API}/clients-api/pullers`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setPullers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);
  const [reassigning, setReassigning] = useState(null);

  const fetchClients = () => {
    fetch(`${API}/clients-api`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setRealClients(Array.isArray(data) ? data : data?.data || []); setInitialLoading(false); })
      .catch(() => setInitialLoading(false));
  };

  // Refetch when tab regains focus (e.g. after creating client in another tab/page)
  useEffect(() => {
    const onFocus = () => fetchClients();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [token]);

  // Fetch real clients from server â€” refetch every time page is visited
  useEffect(() => {
    setInitialLoading(true);
    fetch(`${API}/clients-api`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setRealClients(Array.isArray(data) ? data : data?.data || []); setInitialLoading(false); })
      .catch(() => setInitialLoading(false));
    fetch(`${API}/clients-api/deleted/all`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setDeletedClients(data))
      .catch(() => {});
    // Defer heavy fetches so page renders fast
    setTimeout(() => {
      fetch(`${API}/activity`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(data => setActivityLog(Array.isArray(data) ? data : data?.data || []))
        .catch(() => {});
      fetch(`${API}/documents/client/all`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(data => setAllDocs(Array.isArray(data) ? data : data?.data || []))
        .catch(() => {});
    }, 500);
    if (user.role === 'admin' || user.role === 'team_lead') {
      fetch(`${API}/auth/users`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(data => setReps(data.filter(u => u.role === 'rep' || u.role === 'team_lead' || u.role === 'admin')))
        .catch(() => {});
    }
  }, [token, location.pathname]);

  const handleReassign = async (client, repId) => {
    // repId === '' is the "Unassigned" option - there's no rep to look up
    // for it, so the lookup-then-bail-if-not-found below (needed for the
    // real rep case, to get the full_name to display/send) has to be
    // skipped rather than trip the "not found" bailout. Previously it did
    // trip it: reps.find(r => r.id === '') never matches, so picking
    // Unassigned silently did nothing and the <select> just snapped back to
    // the old value.
    const rep = repId ? reps.find(r => r.id === repId) : null;
    if (repId && !rep) return;
    const newRepId = rep?.id || '';
    const newRepName = rep?.full_name || '';
    setReassigning(client.id);
    try {
      const res = await fetch(`${API}/clients-api/${client.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ assignedRepId: newRepId, assignedRepName: newRepName, manualAssignment: true }),
      });
      if (res.ok) {
        setRealClients(prev => prev.map(c => c.id === client.id ? { ...c, assignedRepId: newRepId, assignedRepName: newRepName } : c));
        setNotification({ type: 'success', msg: rep ? `${client.businessName} assigned to ${rep.full_name}` : `${client.businessName} unassigned` });
      } else {
        setNotification({ type: 'error', msg: 'Failed to reassign' });
      }
    } catch {
      setNotification({ type: 'error', msg: 'Failed to reassign' });
    }
    setReassigning(null);
    setTimeout(() => setNotification(null), 4000);
  };

  // Only use real clients from API â€” no mock data
  const allClients = realClients;

  // Full replacement of the status set, per explicit instruction - the old
  // set (Complete, Submitted for Approval, Submitted to Admin Review,
  // Submitted to Lender, Rejected, Inactive, Requested Additional Docs) is
  // gone entirely, not kept alongside these for backward compat this time.
  // Which actions write which of the first 5 is still being defined -
  // nothing server-side has been rewired to write these values yet, so an
  // existing client still holding an old status will render this dropdown as
  // if nothing is selected until it's moved onto one of these explicitly.
  //
  // Deal Decline / Deal Lost / Funded are manual-only - nothing in the app
  // sets these automatically. Someone picks one from this dropdown by hand
  // once a lender actually responds (declined the deal, the deal fell
  // through, or it funded), same as Inactive worked before this rework.
  //
  // Rejected came back (DECISION_STATUS.rejected server-side, autoSubmit.js)
  // - unlike Deal Decline/Deal Lost/Funded, this one IS set automatically,
  // by the Reject Deal action. A rejected deal no longer shows in Submissions
  // at all (pending/Admin Queue/Sent to Lenders) - this dropdown, and the
  // badge just below it, are the only place it's visible now.
  // FTP: an admin picking a deal up to work it - starts that admin's FTP ->
  // Submitted to UW clock (Admin page, FTP Tracking). Only admins may set it.
  const STATUSES = ['Missing Docs', 'Waiting to be Shopped', 'FTP', 'Admin Review', 'Partial Submission', 'Submitted to UW', 'Rejected', 'Deal Decline', 'Deal Lost', 'Duplicate', 'Funded'];

  // Precompute per-client lookups ONCE per activityLog change, instead of
  // rescanning the whole activity log inside the sort comparator â€” with
  // ~3,700 clients that was up to ~44,000 comparisons x 2 full-array scans
  // each, recomputed on every render. This is what caused the multi-second
  // freeze when opening Clients or clicking into a client.
  const lastActivityById = useMemo(() => {
    const map = new Map();
    for (const a of activityLog) {
      const ts = new Date(a.timestamp || a.createdAt).getTime();
      const prev = map.get(a.clientId);
      if (!prev || ts > prev) map.set(a.clientId, ts);
    }
    return map;
  }, [activityLog]);

  const loggedInIds = useMemo(() => {
    const set = new Set();
    for (const a of activityLog) if (a.eventType === 'login') set.add(a.clientId);
    return set;
  }, [activityLog]);

  // Puller = the very first rep ever assigned to a client.
  // Primary: originalRepName stored on the client at creation time.
  // Fallback: fromRepName of the earliest 'assignment' audit event.
  const pullerByClientId = useMemo(() => {
    const earliest = new Map();
    for (const a of activityLog) {
      if (a.eventType !== 'assignment') continue;
      const ts = new Date(a.timestamp || a.createdAt).getTime();
      const prev = earliest.get(a.clientId);
      if (!prev || ts < prev.ts) earliest.set(a.clientId, { ts, fromRepName: a.fromRepName });
    }
    const map = new Map();
    for (const c of allClients) {
      const name = c.originalRepName || earliest.get(c.id)?.fromRepName || null;
      if (name) map.set(c.id, name);
    }
    return map;
  }, [activityLog, allClients]);

  const firstName = (full) => { const s = txt(full); return s ? s.split(' ')[0] : '—'; };

  const getLastActivity = (c) => {
    const fromLog = lastActivityById.get(c.id);
    if (fromLog != null) return fromLog;
    if (c.createdAt || c.created_at) return new Date(c.createdAt || c.created_at).getTime();
    return 0;
  };

  // Searching only businessName/ownerName left reps unable to find a client by
  // the one identifier they usually have — the email a duplicate-import just
  // rejected, or the client code off a document. Phone is compared digits-only
  // so formatting differences do not hide a match.
  const clients = useMemo(() => allClients
    .filter(c => !repFilter || c.assignedRepId === repFilter)
    .filter(c => matchesClientSearch(c, search))
    .filter(c => !statusFilter || c.status === statusFilter)
    .sort((a, b) => {
      if (sortBy === 'recent') return getLastActivity(b) - getLastActivity(a);
      if (sortBy === 'oldest') return getLastActivity(a) - getLastActivity(b);
      if (sortBy === 'logged_in') {
        const aLogged = loggedInIds.has(a.id) ? 1 : 0;
        const bLogged = loggedInIds.has(b.id) ? 1 : 0;
        return bLogged - aLogged;
      }
      if (sortBy === 'app_completed') {
        const aApp = (a.source === 'docusign' || a.envelopeId) ? 1 : 0;
        const bApp = (b.source === 'docusign' || b.envelopeId) ? 1 : 0;
        return bApp - aApp;
      }
      return 0;
    }), [allClients, repFilter, search, statusFilter, sortBy, lastActivityById, loggedInIds]);

  // Reset visible count when filters change
  useEffect(() => { setVisibleCount(50); }, [search, statusFilter, sortBy]);
  const visibleClients = clients.slice(0, visibleCount);

  // Group docs by client once instead of re-scanning allDocs per visible row
  const docsByClientId = useMemo(() => {
    const map = new Map();
    for (const d of allDocs) {
      if (!map.has(d.clientId)) map.set(d.clientId, []);
      map.get(d.clientId).push(d);
    }
    return map;
  }, [allDocs]);
  const hasMore = visibleCount < clients.length;

  // Assigned Rep filter â€” searchable by name
  const selectedRep = reps.find(r => r.id === repFilter);
  const filteredReps = reps.filter(r => !repSearch || r.full_name?.toLowerCase().includes(repSearch.toLowerCase()));
  const applyRepFilter = (repId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (repId) next.set('rep', repId); else next.delete('rep');
      return next;
    });
    setRepSearch('');
    setShowRepDropdown(false);
  };

  const handleStatusChange = async (client, newStatus) => {
    try {
      const res = await fetch(`${API}/clients-api/${client.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setRealClients(prev => prev.map(c => c.id === client.id ? { ...c, status: newStatus } : c));
      }
    } catch {}
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAddClient = async (e) => {
    e.preventDefault();

    if (!form.businessName || !form.ownerName || !form.email) {
      setNotification({ type: 'error', msg: 'Business name, owner name, and email are required' });
      return;
    }

    setSaving(true);
    let linkShown = false;

    try {
      const idRes = await fetch("https://ffwg2r8ui2.execute-api.us-east-1.amazonaws.com/prod/generate-client-id", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          repCode: user?.repCode || user?.repId || user?.id || "000"
        })
      });

      if (!idRes.ok) {
        throw new Error("Failed to generate client ID");
      }

      const idData = await idRes.json();

      const formWithClientId = {
        ...form,
        clientId: idData.clientId,
        originalRepCode: idData.originalRepCode,
        clientSequence: idData.clientSequence,
        originalRepId: user?.id || user?.repId || "",
        originalRepName: user?.name || user?.full_name || "",
        assignedRepId: user?.id || user?.repId || "",
        assignedRepName: user?.name || user?.full_name || ""
      };

      const res = await fetch(`${API}/clients-api`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formWithClientId)
      });

      const data = await res.json();

      if (res.ok) {
        setRealClients(prev => [data, ...prev]);
        setNotification({ type: 'success', msg: `${form.businessName} added successfully as ${idData.clientId}` });
      } else {
        setNotification({ type: 'error', msg: data.error || 'Failed to add client', existingClient: data?.existingClient || null });
        linkShown = !!data?.existingClient;
      }
    } catch (err) {
      console.error(err);
      setNotification({ type: 'error', msg: 'Failed to add client' });
    } finally {
      setSaving(false);
      setShowAddForm(false);
      setForm(EMPTY_FORM);
      // Longer window when the message carries a link the rep needs to click.
      setTimeout(() => setNotification(null), linkShown ? 15000 : 4000);
    }
  };

  const handleImportFromZoho = async () => {
    if (!zohoUrlInput.trim() || !ZOHO_URL_REGEX.test(zohoUrlInput)) {
      setNotification({ type: 'error', msg: 'Please paste a valid Zoho CRM URL' }); return;
    }
    setZohoImporting(true);
    try {
      const zohoId = extractZohoId(zohoUrlInput);
      const res = await fetch(`${API}/auto-submit/zoho-fields`, { method: 'POST', headers, body: JSON.stringify({ zohoId, zohoUrl: zohoUrlInput }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      const idRes = await fetch('https://ffwg2r8ui2.execute-api.us-east-1.amazonaws.com/prod/generate-client-id', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ repCode: user?.repCode || user?.repId || user?.id || '000' }) });
      if (!idRes.ok) throw new Error('Failed to generate client ID');
      const idData = await idRes.json();
      // Keyed off what /auto-submit/zoho-fields (getAccountFields) actually
      // returns - it used to read data.accountName/data.company/data.email,
      // none of which exist on that response, and data.ownerName, which does
      // exist but is the Zoho CRM record's assigned-user Owner field, not the
      // merchant. businessName and email came through blank on every import;
      // ownerName came through as whichever rep's name is the record's Zoho
      // owner, which reads as a real (wrong) answer rather than an obviously
      // missing one. data.businessName already falls back to Account_Name
      // server-side when Business_Legal_Name is blank - see getAccountFields.
      const payload = { businessName: data.businessName || '', ownerName: data.ownerFullName || '', email: data.ownerEmailPrinciple || data.businessEmail || '', phone: data.phone || '', industry: data.industry || '', state: data.state || '', requestedAmount: data.requestedAmount || '', zohoId, zohoUrl: zohoUrlInput, clientId: idData.clientId, originalRepCode: idData.originalRepCode, clientSequence: idData.clientSequence, originalRepId: user?.id || '', originalRepName: user?.full_name || '', assignedRepId: user?.id || '', assignedRepName: user?.full_name || '' };
      const createRes = await fetch(`${API}/clients-api`, { method: 'POST', headers, body: JSON.stringify(payload) });
      const created = await createRes.json();
      if (!createRes.ok) {
        const err = new Error(created?.error || 'Failed to create client');
        err.existingClient = created?.existingClient || null;
        throw err;
      }
      setRealClients(prev => [created, ...prev]);
      setNotification({ type: 'success', msg: `${payload.businessName || 'Client'} imported from Zoho as ${idData.clientId}` });
      setShowAddForm(false); setZohoUrlInput('');
    } catch (e) {
      setNotification({ type: 'error', msg: `Import failed: ${e.message}`, existingClient: e.existingClient || null });
    } finally {
      setZohoImporting(false);
      // A notification carrying a link needs long enough to actually be clicked.
      setTimeout(() => setNotification(null), 15000);
    }
  };

  const openReminder = (client) => {
    setReminderModal({ client, category: '' });
    setReminderMsg('');
  };

  const sendReminder = async () => {
    if (!reminderModal.category) return;
    setSendingReminder(true);
    const cat = DOC_CATEGORIES.find(c => c.id === reminderModal.category);
    try {
      await fetch(`${API}/clients-api/${reminderModal.client.id}/reminder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          category: reminderModal.category,
          categoryLabel: cat?.label,
          customMessage: reminderMsg || `Please upload your ${cat?.label} at your earliest convenience.`,
        }),
      });
      setNotification({ type: 'success', msg: `Reminder sent to ${reminderModal.client.email}` });
    } catch {
      setNotification({ type: 'success', msg: `Reminder queued for ${reminderModal.client.ownerName}` });
    } finally {
      setSendingReminder(false);
      setReminderModal(null);
      setTimeout(() => setNotification(null), 4000);
    }
  };

  const handleSoftDelete = async (client) => {
    try {
      await fetch(`${API}/clients-api/${client.id}`, { method: 'DELETE', headers });
      setRealClients(prev => prev.filter(c => c.id !== client.id));
      setDeletedClients(prev => [{ ...client, deleted: true, deletedAt: new Date().toISOString() }, ...prev]);
      setNotification({ type: 'success', msg: `${client.businessName} moved to deleted` });
    } catch {
      setNotification({ type: 'error', msg: 'Failed to delete client' });
    }
    setDeleteConfirm(null);
    setTimeout(() => setNotification(null), 4000);
  };

  const handleRestore = async (client) => {
    try {
      await fetch(`${API}/clients-api/${client.id}/restore`, { method: 'POST', headers });
      setDeletedClients(prev => prev.filter(c => c.id !== client.id));
      setRealClients(prev => [{ ...client, deleted: false }, ...prev]);
      setNotification({ type: 'success', msg: `${client.businessName} restored` });
    } catch {
      setNotification({ type: 'error', msg: 'Failed to restore client' });
    }
    setTimeout(() => setNotification(null), 4000);
  };

  const handlePermanentDelete = async (client) => {
    try {
      await fetch(`${API}/clients-api/${client.id}/permanent`, { method: 'DELETE', headers });
      setDeletedClients(prev => prev.filter(c => c.id !== client.id));
      setNotification({ type: 'success', msg: `${client.businessName} permanently deleted` });
    } catch {
      setNotification({ type: 'error', msg: 'Failed to permanently delete' });
    }
    setPermDeleteConfirm(null);
    setTimeout(() => setNotification(null), 4000);
  };

  // Deleted Clients: permanently delete every ticked row in one request.
  const handleBulkPermanentDelete = async (ids) => {
    try {
      const res = await fetch(`${API}/clients-api/permanent-delete-bulk`, {
        method: 'POST', headers, body: JSON.stringify({ ids }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || `HTTP ${res.status}`);
      const gone = new Set((d.removed || []).map(String));
      setDeletedClients(prev => prev.filter(c => !gone.has(String(c.id))));
      setSelectedDeleted(new Set());
      setNotification({ type: 'success', msg: `${gone.size} client${gone.size === 1 ? '' : 's'} permanently deleted` });
    } catch (e) {
      setNotification({ type: 'error', msg: `Failed to permanently delete: ${e.message}` });
    }
    setPermDeleteConfirm(null);
    setTimeout(() => setNotification(null), 4000);
  };

  if (initialLoading) return <SkeletonClients />;

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 flex items-start gap-3 max-w-md rounded-xl px-4 py-3 shadow-lg border ${
          notification.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          {notification.type === 'success'
            ? <CheckCircle2 size={16} className="text-green-600 shrink-0 mt-0.5" />
            : <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
          }
          <div className="min-w-0">
            <p className={`text-sm font-medium ${notification.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
              {notification.msg}
            </p>
            {/* On a duplicate-email collision the rep needs to reach the client
                that already exists, not just be told that it does. */}
            {notification.existingClient?.id && (
              <Link
                to={`/clients/${notification.existingClient.id}`}
                onClick={() => setNotification(null)}
                className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2"
              >
                Open {notification.existingClient.businessName || notification.existingClient.ownerName || notification.existingClient.email}
                {notification.existingClient.clientCode && (
                  <span className="font-normal text-gray-500">· {notification.existingClient.clientCode}</span>
                )}
              </Link>
            )}
          </div>
          <button onClick={() => setNotification(null)} className="ml-2 text-gray-400 shrink-0"><X size={14} /></button>
        </div>
      )}

      {/* Reminder Modal */}
      {reminderModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-gray-900 font-semibold text-base flex items-center gap-2">
                <Bell size={16} className="text-blue-600" /> Send Reminder
              </h2>
              <button onClick={() => setReminderModal(null)} className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
            </div>

            <div className="bg-gray-50 rounded-xl px-4 py-3 mb-4">
              <p className="text-gray-500 text-xs">Sending to</p>
              <p className="text-gray-900 font-semibold text-sm">{reminderModal.client.businessName}</p>
              <p className="text-gray-400 text-xs">{reminderModal.client.email}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Document Needed <span className="text-red-400">*</span></label>
                <select
                  value={reminderModal.category}
                  onChange={e => setReminderModal(m => ({ ...m, category: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Select document type...</option>
                  {DOC_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Custom Message (optional)</label>
                <textarea
                  value={reminderMsg}
                  onChange={e => setReminderMsg(e.target.value)}
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Add a personal note to the client..."
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
              <p className="text-gray-400 text-xs">Client will receive an email with an upload link</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setReminderModal(null)} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button
                  onClick={sendReminder}
                  disabled={!reminderModal.category || sendingReminder}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-xl transition-colors font-medium"
                >
                  <Send size={13} /> {sendingReminder ? 'Sending...' : 'Send Reminder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Clients</h1>
          <p className="text-gray-400 text-sm mt-0.5">{clients.length} client{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          {user.role === 'team_lead' && (
            <span className="text-gray-400 text-xs">Team leads can see their team's pipeline</span>
          )}
          {user.role === 'admin' && (
            <button
              onClick={() => setShowAdjust(v => !v)}
              className={`flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm font-medium border ${showAdjust ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              <SlidersHorizontal size={15} /> Adjust
            </button>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm font-medium"
          >
            <Plus size={15} /> Add Client
          </button>
        </div>
      </div>

      {/* Admin: adjust a deal's closer / puller */}
      {showAdjust && user.role === 'admin' && (
        <AdjustPanel
          clients={allClients}
          reps={reps}
          headers={headers}
          pullerNameOf={c => pullerByClientId.get(c.id) || ''}
          onSaved={(updated) => setRealClients(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c))}
          onClose={() => setShowAdjust(false)}
        />
      )}

      {/* Add Client Form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h2 className="text-gray-900 font-semibold text-sm">New Client</h2>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold">
                <button type="button" onClick={() => setAddMode('manual')} className={`px-3 py-1.5 transition-colors ${addMode === 'manual' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>Manual</button>
                <button type="button" onClick={() => setAddMode('zoho')} className={`px-3 py-1.5 transition-colors ${addMode === 'zoho' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>From Zoho URL</button>
              </div>
            </div>
            <button onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); setZohoUrlInput(''); }} className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
          </div>
          {addMode === 'zoho' ? (
            <div className="p-6 space-y-4">
              <p className="text-xs text-gray-500">Paste the Zoho CRM record URL. Orbit will pull their info automatically.</p>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Zoho CRM URL <span className="text-red-400">*</span></label>
                <input value={zohoUrlInput} onChange={e => setZohoUrlInput(e.target.value)} className={inputCls} placeholder="https://crm.zoho.com/crm/org.../tab/Accounts/123..." />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => { setShowAddForm(false); setZohoUrlInput(''); }} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={handleImportFromZoho} disabled={zohoImporting || !zohoUrlInput.trim()} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl font-medium shadow-sm">
                  {zohoImporting ? 'Importing...' : 'Import from Zoho'}
                </button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleAddClient} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Business Name <span className="text-red-400">*</span></label>
                <input value={form.businessName} onChange={e => set('businessName', e.target.value)} className={inputCls} placeholder="e.g. Smith Auto Repair" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Owner Name <span className="text-red-400">*</span></label>
                <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} className={inputCls} placeholder="e.g. John Smith" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Email <span className="text-red-400">*</span></label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} placeholder="john@business.com" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} placeholder="(555) 000-0000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Industry</label>
                <select value={form.industry} onChange={e => set('industry', e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="">Select industry...</option>
                  {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">State</label>
                <input value={form.state} onChange={e => set('state', e.target.value)} className={inputCls} placeholder="NY" maxLength={2} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Puller</label>
                <select value={form.pullerId} onChange={e => set('pullerId', e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="">Me (same as closer)</option>
                  {pullers.filter(p => p.id !== user.id).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Requested Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input type="number" value={form.requestedAmount} onChange={e => set('requestedAmount', e.target.value)} className={`${inputCls} pl-7`} placeholder="75000" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <p className="text-gray-400 text-xs">Client will be added to your portfolio immediately</p>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); }} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-5 py-2.5 rounded-xl transition-colors font-medium shadow-sm">
                  {saving ? 'Adding...' : 'Add Client'}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm transition-all" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-white border border-gray-200 text-gray-700 text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm">
          <option value="">{t('All Statuses')}</option>
          {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-white border border-gray-200 text-gray-700 text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm">
          <option value="recent">{t('Most Recent Activity')}</option>
          <option value="oldest">{t('Least Recent Activity')}</option>
          <option value="logged_in">{t('Client Logged In')}</option>
          <option value="app_completed">{t('Application Completed')}</option>
        </select>
        {(user.role === 'admin' || user.role === 'team_lead') && (
          <div className="relative">
            {selectedRep ? (
              <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-xl pl-3 pr-2 py-2 shadow-sm">
                <span className="font-medium truncate max-w-[140px]">{selectedRep.full_name}</span>
                <button onClick={() => applyRepFilter(null)} className="text-blue-400 hover:text-blue-700 shrink-0" title="Clear rep filter">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <input
                  value={repSearch}
                  onChange={e => { setRepSearch(e.target.value); setShowRepDropdown(true); }}
                  onFocus={() => setShowRepDropdown(true)}
                  onBlur={() => setTimeout(() => setShowRepDropdown(false), 150)}
                  placeholder={t('Filter by rep...')}
                  className="bg-white border border-gray-200 text-gray-700 placeholder-gray-400 text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm w-44"
                />
                {showRepDropdown && filteredReps.length > 0 && (
                  <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-lg w-56 max-h-64 overflow-y-auto">
                    {filteredReps.map(r => (
                      <button
                        key={r.id}
                        onMouseDown={() => applyRepFilter(r.id)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        {r.full_name}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Selection bar */}
      {selectedClients.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm">
          <span className="font-semibold text-blue-700">{selectedClients.size} client{selectedClients.size !== 1 ? 's' : ''} selected</span>
          <button onClick={() => setSelectedClients(new Set())} className="ml-auto text-xs text-blue-500 hover:text-blue-700 font-medium">Clear</button>
        </div>
      )}

      {/* Table */}
      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-gray-100 rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1060px]">
          <thead>
            <tr className="border-b border-gray-50 bg-gray-50/50">
              <th className="py-3 pl-4 pr-2 w-8"></th>
              {['Business','Owner',...(user.role === 'admin' || user.role === 'team_lead' ? ['Assigned Rep','Puller'] : []),'Status','Activity','Docs','Miss.',''].map(h => (
                <th key={h} className="text-left py-3 px-2 text-gray-400 font-medium text-xs uppercase tracking-wide">{t(h)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleClients.map(c => {
              const docs = docsByClientId.get(c.id) || [];
              const uploadedCats = new Set(docs.map(d => d.category));
              const missing = DOC_CATEGORIES.filter(cat => cat.required && !uploadedCats.has(cat.id));
              const isSelected = selectedClients.has(c.id);
              return (
                <tr key={c.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors group ${isSelected ? 'bg-blue-50/40' : ''}`}>
                  <td className="py-3.5 pl-4 pr-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => toggleClientSelect(c.id, e)}
                      onClick={e => e.stopPropagation()}
                      className="w-4 h-4 accent-blue-600 cursor-pointer"
                    />
                  </td>
                  <td className="py-3.5 px-3">
                    <Link to={`/clients/${c.id}`} className="block no-underline text-inherit hover:opacity-70 cursor-pointer">
                      <p className="text-gray-900 font-medium">{txt(c.businessName)}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{txt(c.industry)}{c.state ? ` · ${txt(c.state)}` : ''}</p>
                    </Link>
                  </td>
                  <td className="py-3.5 px-3 text-gray-600 text-sm">
                    <Link to={`/clients/${c.id}`} className="no-underline text-inherit hover:opacity-70 cursor-pointer">{txt(c.ownerName)}</Link>
                  </td>
                  {(user.role === 'admin' || user.role === 'team_lead') && (
                    <>
                      <td className="py-3.5 px-2">
                        {user.role === 'admin' ? (
                          <select
                            value={c.assignedRepId || ''}
                            onChange={e => handleReassign(c, e.target.value)}
                            disabled={reassigning === c.id}
                            className="bg-gray-50 border border-gray-200 text-gray-700 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 min-w-[110px]"
                          >
                            <option value="">Unassigned</option>
                            {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                          </select>
                        ) : (
                          <span className="text-gray-700 text-xs font-medium">{txt(c.assignedRepName) || 'Unassigned'}</span>
                        )}
                      </td>
                      <td className="py-3.5 px-2">
                        <span className="text-gray-500 text-xs">{firstName(pullerByClientId.get(c.id))}</span>
                      </td>
                    </>
                  )}
                  <td className="py-3.5 px-2">
                    <select
                      value={c.status || 'Working'}
                      onChange={e => handleStatusChange(c, e.target.value)}
                      className={`text-xs font-medium rounded-lg px-1.5 py-1 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 max-w-[106px] ${
                        c.status === 'Missing Docs'              ? 'bg-red-50 border-red-200 text-red-600'
                        : c.status === 'Waiting to be Shopped'   ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        : c.status === 'FTP'                     ? 'bg-violet-50 border-violet-200 text-violet-700'
                        : c.status === 'Admin Review'            ? 'bg-cyan-50 border-cyan-200 text-cyan-700'
                        : c.status === 'Partial Submission'      ? 'bg-orange-50 border-orange-200 text-orange-600'
                        : c.status === 'Submitted to UW'         ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : c.status === 'Rejected'                ? 'bg-red-100 border-red-300 text-red-800'
                        : c.status === 'Deal Decline'            ? 'bg-rose-50 border-rose-200 text-rose-700'
                        : c.status === 'Deal Lost'               ? 'bg-red-800 border-red-900 text-white'
                        : c.status === 'Duplicate'               ? 'bg-stone-100 border-stone-300 text-stone-700'
                        : c.status === 'Funded'                  ? 'bg-green-700 border-green-800 text-white'
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                      }`}
                    >
                      {STATUSES.map(s => {
                        // FTP is for admins to set (the server refuses it from
                        // anyone else); still listed when it is the current value.
                        if (s === 'FTP' && user.role !== 'admin' && c.status !== 'FTP') return null;
                        const label = {
                          'Waiting to be Shopped': 'Waiting to Shop',
                          'Partial Submission':    'Partial Subm.',
                        }[s] || s;
                        // Explicit colors: the select itself turns white-on-dark-red for
                        // Deal Lost, and options inherit that color otherwise.
                        return <option key={s} value={s} className="bg-white text-gray-900">{label}</option>;
                      })}
                    </select>
                  </td>
                  <td className="py-3.5 px-2">
                    {(() => {
                      const clientActivity = activityLog.filter(a => a.clientId === c.id).sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt));
                      if (clientActivity.length > 0) {
                        const last = clientActivity[0];
                        const desc = last.eventType === 'login' ? 'Logged in' : last.eventType === 'upload' ? 'Doc uploaded' : txt(last.description).slice(0, 20);
                        const time = new Date(last.timestamp || last.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        return <span className="text-gray-500 text-xs">{desc} · {time}</span>;
                      }
                      if (c.source === 'docusign' || c.envelopeId) return <span className="text-blue-500 text-xs">App done</span>;
                      return <span className="text-gray-300 text-xs">None</span>;
                    })()}
                  </td>
                  <td className="py-3.5 px-2">
                    <div className="relative group/docs">
                      <div className="flex items-center gap-1.5 text-gray-500 text-sm cursor-pointer">
                        <FileText size={13} className="text-gray-400" /> {docs.length}
                      </div>
                      {docs.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 z-40 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 hidden group-hover/docs:block">
                          <p className="text-xs font-semibold text-gray-900 mb-2">Documents ({docs.length})</p>
                          <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {docs.slice(0, 8).map(d => (
                              <div key={d.id} className="flex items-center gap-2 text-xs">
                                <FileText size={10} className="text-gray-400 shrink-0" />
                                <span className="text-gray-600 truncate">{txt(d.fileName) || txt(d.category) || 'Document'}</span>
                              </div>
                            ))}
                            {docs.length > 8 && <p className="text-gray-400 text-xs">+{docs.length - 8} more</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3.5 px-2">
                    {missing.length > 0
                      ? <div className="flex items-center gap-1 text-red-500 text-xs font-medium"><AlertCircle size={12} />{missing.length}</div>
                      : <span className="text-green-600 text-xs font-medium">✓</span>
                    }
                  </td>
                  <td className="py-3.5 px-2 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.preventDefault(); setDeleteConfirm(c); }} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Delete client">
                        <Trash2 size={13} />
                      </button>
                      <Link to={`/clients/${c.id}`} className="flex items-center gap-1 text-blue-600 text-xs font-medium hover:opacity-70">
                        Open <ArrowRight size={12} />
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {clients.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm">No clients found.</p>
            <button onClick={() => setShowAddForm(true)} className="mt-3 text-blue-600 text-sm font-medium hover:opacity-70">+ Add your first client</button>
          </div>
        )}
        {hasMore && (
          <div className="text-center py-4 border-t border-gray-50">
            <button onClick={() => setVisibleCount(v => Math.min(v + 50, clients.length))} className="text-blue-600 text-sm font-medium hover:opacity-70">
              Load more ({clients.length - visibleCount} remaining)
            </button>
          </div>
        )}
      </div>

      {/* Mobile card view */}
      <div className="md:hidden space-y-3">
        {visibleClients.map(c => {
          const docs = docsByClientId.get(c.id) || [];
          const isSelected = selectedClients.has(c.id);
          return (
            <div key={c.id} className={`relative bg-white border rounded-xl p-4 shadow-sm transition-colors ${isSelected ? 'border-blue-300 bg-blue-50/30' : 'border-gray-100'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2.5 min-w-0 flex-1">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={e => toggleClientSelect(c.id, e)}
                    className="w-4 h-4 accent-blue-600 cursor-pointer mt-0.5 shrink-0"
                  />
                  <Link to={`/clients/${c.id}`} className="min-w-0 flex-1">
                    <p className="text-gray-900 font-semibold text-sm truncate">{txt(c.businessName)}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{txt(c.ownerName)}</p>
                  </Link>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ml-2 ${
                  c.status === 'Missing Docs'             ? 'bg-red-50 border-red-200 text-red-600'
                  : c.status === 'Waiting to be Shopped'  ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                  : c.status === 'FTP'                    ? 'bg-violet-50 border-violet-200 text-violet-700'
                  : c.status === 'Admin Review'           ? 'bg-cyan-50 border-cyan-200 text-cyan-700'
                  : c.status === 'Partial Submission'     ? 'bg-orange-50 border-orange-200 text-orange-600'
                  : c.status === 'Submitted to UW'        ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : c.status === 'Rejected'               ? 'bg-red-100 border-red-300 text-red-800'
                  : c.status === 'Deal Decline'           ? 'bg-rose-50 border-rose-200 text-rose-700'
                  : c.status === 'Deal Lost'              ? 'bg-red-800 border-red-900 text-white'
                  : c.status === 'Duplicate'              ? 'bg-stone-100 border-stone-300 text-stone-700'
                  : c.status === 'Funded'                 ? 'bg-green-700 border-green-800 text-white'
                  : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}>{c.status || 'Waiting to be Shopped'}</span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 pl-6">
                {c.industry && <span>{txt(c.industry)}</span>}
                {c.state && <span>{txt(c.state)}</span>}
                <span>{docs.length} docs</span>
              </div>
            </div>
          );
        })}
        {clients.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No clients found.</p>}
        {hasMore && (
          <button onClick={() => setVisibleCount(v => Math.min(v + 50, clients.length))} className="w-full py-3 text-blue-600 text-sm font-medium">
            Load more ({clients.length - visibleCount} remaining)
          </button>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-sm p-6">
            <h2 className="text-gray-900 font-semibold text-base mb-2">Delete Client?</h2>
            <p className="text-gray-500 text-sm mb-1">Are you sure you want to delete <span className="font-semibold text-gray-700">{deleteConfirm.businessName}</span>?</p>
            <p className="text-gray-400 text-xs mb-5">This will move them to the Deleted Clients folder. You can restore them from there.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleSoftDelete(deleteConfirm)} className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-xl font-medium">
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent delete confirmation modal */}
      {permDeleteConfirm && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-red-200 w-full max-w-sm p-6">
            <h2 className="text-red-600 font-semibold text-base mb-2">Permanently Delete?</h2>
            <p className="text-gray-500 text-sm mb-1">This will permanently remove <span className="font-semibold text-gray-700">{permDeleteConfirm.bulkIds ? `${permDeleteConfirm.bulkIds.length} client${permDeleteConfirm.bulkIds.length === 1 ? '' : 's'}` : permDeleteConfirm.businessName}</span> and all associated data.</p>
            <p className="text-red-400 text-xs mb-5">This action cannot be undone.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setPermDeleteConfirm(null)} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50">Cancel</button>
              <button onClick={() => permDeleteConfirm.bulkIds ? handleBulkPermanentDelete(permDeleteConfirm.bulkIds) : handlePermanentDelete(permDeleteConfirm)} className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-xl font-medium">
                <Trash2 size={13} /> Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deleted clients section */}
      {deletedClients.length > 0 && (
        <div className="mt-6">
          <button onClick={() => setShowDeleted(v => !v)} className="flex items-center gap-2 text-gray-400 text-sm hover:text-gray-600 transition-colors mb-3">
            <Trash2 size={14} />
            Deleted Clients ({deletedClients.length})
            <span className="text-xs">{showDeleted ? '▲' : '▼'}</span>
          </button>
          {showDeleted && (
            <div className="bg-white border border-red-100 rounded-2xl shadow-sm overflow-hidden">
              {selectedDeleted.size > 0 && (
                <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-red-100 bg-red-50/50">
                  <span className="text-xs font-medium text-red-700">{selectedDeleted.size} selected</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelectedDeleted(new Set())}
                      className="text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg">Clear</button>
                    <button onClick={() => setPermDeleteConfirm({ bulkIds: [...selectedDeleted] })}
                      className="flex items-center gap-1 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium">
                      <Trash2 size={11} /> Delete selected forever
                    </button>
                  </div>
                </div>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-red-50 bg-red-50/30">
                    <th className="py-3 pl-5 pr-0 w-8">
                      <input type="checkbox"
                        title="Select all deleted clients"
                        checked={deletedClients.length > 0 && deletedClients.every(c => selectedDeleted.has(c.id))}
                        onChange={e => setSelectedDeleted(e.target.checked ? new Set(deletedClients.map(c => c.id)) : new Set())}
                        className="rounded border-gray-300" />
                    </th>
                    {['Business', 'Owner', 'Deleted', ''].map(h => (
                      <th key={h} className="text-left py-3 px-5 text-gray-400 font-medium text-xs uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deletedClients.map(c => (
                    <tr key={c.id} className="border-b border-red-50 hover:bg-red-50/20 transition-colors">
                      <td className="py-3.5 pl-5 pr-0 w-8">
                        <input type="checkbox"
                          checked={selectedDeleted.has(c.id)}
                          onChange={e => setSelectedDeleted(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                            return next;
                          })}
                          className="rounded border-gray-300" />
                      </td>
                      <td className="py-3.5 px-5 text-gray-500">{txt(c.businessName)}</td>
                      <td className="py-3.5 px-5 text-gray-400 text-sm">{txt(c.ownerName)}</td>
                      <td className="py-3.5 px-5 text-gray-400 text-xs">{c.deletedAt ? new Date(c.deletedAt).toLocaleDateString() : 'â€”'}</td>
                      <td className="py-3.5 px-5">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleRestore(c)} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 border border-green-200 hover:border-green-300 px-2.5 py-1.5 rounded-lg transition-colors">
                            <RotateCcw size={11} /> Restore
                          </button>
                          <button onClick={() => setPermDeleteConfirm(c)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 px-2.5 py-1.5 rounded-lg transition-colors">
                            <Trash2 size={11} /> Delete Forever
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Admin-only "Adjust" panel: pick a business, change its closer (assigned rep)
 * and/or its puller, save. Stays open after a save so the next deal can be
 * adjusted straight away. Only the fields that changed are sent.
 */
function AdjustPanel({ clients, reps, headers, pullerNameOf, onSaved, onClose }) {
  const [query, setQuery] = useState('');
  const [clientId, setClientId] = useState('');
  const [closerId, setCloserId] = useState('');
  const [pullerId, setPullerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type, text }

  const client = clients.find(c => c.id === clientId) || null;
  const pullerName = (c) => c?.pullerName || pullerNameOf(c) || '';
  // The puller is stored by id on newer deals and only by name on older ones.
  const currentPullerId = (c) => c?.pullerId || reps.find(r => r.full_name && r.full_name === pullerName(c))?.id || '';

  const matches = useMemo(
    () => (!query.trim() || client ? [] : clients.filter(c => matchesClientSearch(c, query)).slice(0, 8)),
    [query, clients, client],
  );

  const pick = (c) => {
    setClientId(c.id);
    setQuery(c.businessName || c.ownerName || '');
    setCloserId(c.assignedRepId || '');
    setPullerId(currentPullerId(c));
    setMessage(null);
  };

  const changes = client
    ? {
        ...(closerId !== (client.assignedRepId || '') ? { closerId } : {}),
        ...(pullerId !== currentPullerId(client) ? { pullerId } : {}),
      }
    : {};
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!client || !dirty) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API}/clients-api/${client.id}/adjust`, { method: 'POST', headers, body: JSON.stringify(changes) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Failed to save');
      onSaved(body);
      const name = id => reps.find(r => r.id === id)?.full_name || 'none';
      const parts = [
        'closerId' in changes && `closer → ${name(changes.closerId)}`,
        'pullerId' in changes && `puller → ${name(changes.pullerId)}`,
      ].filter(Boolean);
      setMessage({ type: 'success', text: `${client.businessName}: ${parts.join(', ')}` });
      setClientId('');
      setQuery('');
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
    setSaving(false);
  };

  const selectCls = 'w-full bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-50';
  const labelCls = 'block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide';

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-gray-900 font-semibold text-sm">Adjust puller / closer</h2>
          <p className="text-gray-400 text-xs mt-0.5">Admin only. Changes are logged to the client&apos;s audit trail.</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-4 items-end">
        <div className="relative">
          <label className={labelCls}>Business</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={e => { setQuery(e.target.value); setClientId(''); setMessage(null); }}
              placeholder="Search business, owner, email..."
              className={`${selectCls} pl-9`}
            />
          </div>
          {matches.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
              {matches.map(c => (
                <button key={c.id} type="button" onClick={() => pick(c)} className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0">
                  <div className="text-sm text-gray-900 font-medium">{c.businessName || '(no business name)'}</div>
                  <div className="text-xs text-gray-400">
                    {c.ownerName}{c.ownerName ? ' · ' : ''}Closer: {c.assignedRepName || 'Unassigned'} · Puller: {pullerName(c) || '—'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className={labelCls}>Closer</label>
          <select value={closerId} onChange={e => setCloserId(e.target.value)} disabled={!client} className={selectCls}>
            <option value="">Unassigned</option>
            {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Puller</label>
          <select value={pullerId} onChange={e => setPullerId(e.target.value)} disabled={!client} className={selectCls}>
            <option value="">{client && !currentPullerId(client) && pullerName(client) ? `${pullerName(client)} (not a user)` : 'None'}</option>
            {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm px-5 py-2.5 rounded-xl transition-colors font-medium"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      {message && (
        <div className={`mx-6 mb-5 -mt-1 text-xs font-medium flex items-center gap-1.5 ${message.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
          {message.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {message.text}
        </div>
      )}
    </div>
  );
}
